import { useTimeCards } from '@hooks/useTimeCards'
import { col, weekGridClasses } from '@utils/styles'
import classNames from 'classnames'
import { WeeklyProgressGauge } from 'components/indicators/weekly-progress-gauge'
import { Spinner } from 'components/loaders'
import { FC, useMemo } from 'react'
import { WorkedAndAllocated } from './WorkedAndAllocated'
import { useAbsences } from '@hooks/useAbsences'
import { Day } from './Day'
import { useTotalsForWeek } from '@features/time-logging/hooks/useTotalsForWeek'
import { DayAnnotation } from './day-annotation'
import { useWeekDates } from '@hooks/useWeekDates'
import { useWeekStartsOn } from '@hooks/useWeekStartsOn'
import { useHolidays } from '@features/time-logging/hooks/useHolidays'
import { useEmployee } from '@hooks/useEmployee'
import { dateIntersectsAbsence } from '@utils/absence'
import { useUserId } from '@features/time-logging/hooks/useUserId'
import { useDayLocks } from '@hooks/useDayLocks'
import { getDayOfWeek } from '@utils/dates'

interface Props {
  onTimeOffClick: () => void
  readOnly: boolean
}

export const TimeCards: FC<Props> = ({ onTimeOffClick, readOnly }) => {
  const userId = useUserId()
  const weekStartsOn = useWeekStartsOn()
  const { range, start, end } = useWeekDates({ weekStartsOn })
  const employeeQuery = useEmployee({ userId, startDate: start })
  const timeCardsQuery = useTimeCards({ userId, start, end })
  const holidaysQuery = useHolidays({ start, end })
  const { data: absences = [] } = useAbsences({ userId, start, end })
  const { absenceSeconds, holidaySeconds, allocatedSeconds } = useTotalsForWeek(
    { start, end },
  )
  const { data: dayLocks } = useDayLocks({ userId, start, end })

  const cardsLoading = useMemo(
    () => timeCardsQuery.data === undefined || employeeQuery.data === undefined,
    [timeCardsQuery, employeeQuery],
  )

  const cards = useMemo(() => {
    if (timeCardsQuery.data === undefined) return []

    let activeCardCount = 0

    const cards: ({ active: boolean; numLabel: number } & TimeCard)[] = []

    timeCardsQuery.data.forEach((card) => {
      const active = card.id !== 0
      const numLabel = active ? activeCardCount + 1 : activeCardCount
      cards.push({ ...card, active, numLabel })
      activeCardCount = numLabel
    })

    return cards
  }, [timeCardsQuery.data])

  const totalHours = Object.values(
    employeeQuery.data?.workSchedule ?? {},
  ).reduce((sum, hoursPerDay) => sum + parseFloat(hoursPerDay), 0)

  return (
    <div className="px-8 py-5 border-b bg-neutral-50 border-neutral-200">
      <div className={`mb-[-2px] ${weekGridClasses(weekStartsOn)}`}>
        {range.map((date) => (
          <DayAnnotation
            key={`annotations-${date.toString()}`}
            date={date}
            absences={absences}
            holidays={holidaysQuery?.data ?? []}
            onTimeOffClick={onTimeOffClick}
            weekStartDate={start}
            weekEndDate={end}
          />
        ))}
      </div>
      <div className={weekGridClasses(weekStartsOn)}>
        <WorkedAndAllocated />
        {cardsLoading
          ? range.map((date) => (
              <div
                key={date.toString()}
                className={classNames(
                  'flex items-center justify-center',
                  col(date),
                )}
              >
                <Spinner />
              </div>
            ))
          : cards.map((card) => (
              <div
                className={`flex px-1 ${col(card.date)}`}
                key={`time-card-slot-${card.date.toISO()}`}
              >
                <Day
                  date={card.date}
                  numberLabel={card.numLabel}
                  absences={absences.filter((absence) =>
                    dateIntersectsAbsence(card.date, absence),
                  )}
                  readOnly={readOnly}
                  lock={dayLocks?.find((lock) =>
                    lock.date.hasSame(card.date, 'day'),
                  )}
                  scheduledWorkHours={
                    employeeQuery.data?.workSchedule[getDayOfWeek(card.date)]
                  }
                />
              </div>
            ))}
        <div className="flex flex-col justify-end grid-in-totalByTask">
          <WeeklyProgressGauge
            numAbsenceSeconds={absenceSeconds}
            numHolidaySeconds={holidaySeconds}
            numWorkSeconds={allocatedSeconds}
            totalHours={totalHours}
            loading={cardsLoading}
            className="grow"
          />
        </div>
      </div>
    </div>
  )
}
